import React, { useEffect, useState } from "react"
import { useAppContext } from "../contexts/AppContext"
import QuadriRestApi, { TaskType, TaskTypeDefinition } from "../apis/quadriRestApi"
import { ModusTreeViewItem } from "@trimble-oss/modus-react-components"
import { TaskTypeDefinitionsCache } from "../apis/TaskTypeDefinitionsCache"

interface TaskTreeItemComponentProps {
  taskTypeId: string
  taskType: TaskType
  parentExpanded: boolean
  setSelectedNode: (taskType: TaskType) => void
}

const TaskTreeItemComponent: React.FC<TaskTreeItemComponentProps> = ({
  taskTypeId,
  taskType,
  parentExpanded,
  setSelectedNode
}) => {
  const { projectID, accessToken, projectLocation, API } = useAppContext()
  const [loading, setLoading] = useState(true)
  const [taskTypeSubTree, setTaskTypeSubTree] = useState<TaskType>()
  const [taskTypeWithProperties, setTaskTypeWithProperties] = useState<TaskType>()
  const [expanded, setExpanded] = useState<boolean>(false)
  const [taskTypeDefinition, setTaskTypeDefinition] = useState<TaskTypeDefinition>()
  const numLevelsToFetch = 2

  useEffect(() => {
    const fetchTask = async () => {
      try {
        if (taskType.header.taskTypeTag) {
          const taskTypeDefinition = TaskTypeDefinitionsCache.getInstance().getTaskTypeDefinition(
            taskType.header.taskTypeTag
          )
          setTaskTypeDefinition(taskTypeDefinition)
        }

        //SingleTaskTypes have no children
        if (taskType.header.taskClass === "Default") return

        //got children from parent
        if (taskType.children && taskType.children.length !== 0) return

        //don't fetch if parent is not expanded
        if (!parentExpanded) return

        //fetch only once
        if (taskTypeSubTree) return

        setLoading(true)
        const restApi = new QuadriRestApi()
        await restApi.initialize(accessToken, projectLocation)
        const subTree = await restApi.getSubTaskTree(projectID, taskTypeId, numLevelsToFetch)
        setTaskTypeSubTree(subTree)
      } catch (error) {
        console.error("Error fetching task:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchTask()
  }, [taskTypeId, taskType, expanded, parentExpanded, accessToken, projectID, projectLocation, taskTypeSubTree])

  useEffect(() => {
    if (taskTypeWithProperties) {
      setSelectedNode(taskTypeWithProperties)
    }
  }, [taskTypeWithProperties, setSelectedNode])

  const handleNodeClick = async (event: any) => {
    event.stopPropagation()
    if (!taskTypeWithProperties) {
      const restApi = new QuadriRestApi()
      await restApi.initialize(accessToken, projectLocation)
      const detailsTaskType = await restApi.getTask(projectID, taskTypeId)
      setTaskTypeWithProperties(detailsTaskType)
    } else {
      setSelectedNode(taskTypeWithProperties)
    }
  }

  const handleNodeExpandToggle = (event: any) => {
    event.stopPropagation()
    setExpanded((prevExpanded) => !prevExpanded)
  }

  const addDummyNode = () => {
    //SingleTaskTypes have no children
    if (taskType.header.taskClass === "Default") return false

    //children are not loaded yet
    return (!taskType.children || taskType.children.length === 0) && !taskTypeSubTree
  }
  const summaryTaskSvg = `
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" >
    <defs>
      <style>.cls-1{fill:#363545;}</style>
    </defs>
    <title>SummaryTask_24</title>
    <path class="cls-1" d="M21,22H3V2H21ZM5,20H19V4H5Z"/>
    <rect class="cls-1" x="6" y="7" width="2" height="2"/>
    <rect class="cls-1" x="10" y="7" width="8" height="2"/>
    <rect class="cls-1" x="6" y="11" width="2" height="2"/>
    <rect class="cls-1" x="10" y="11" width="8" height="2"/>
    <rect class="cls-1" x="6" y="15" width="2" height="2"/>
    <rect class="cls-1" x="10" y="15" width="8" height="2"/>
  </svg>`

  return (
    <ModusTreeViewItem
      key={taskTypeId}
      nodeId={taskTypeId}
      label={(taskTypeSubTree || taskType)?.header.name || "Not loaded"}
      onItemClick={(event) => handleNodeClick(event)}
      onItemExpandToggle={(event) => handleNodeExpandToggle(event)}
    >
      {/* <span
        slot="itemIcon"
        style={{
          height: "32px",
          width: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginLeft: "0px"
        }}
      >
        <img
          src={`data:image/svg+xml;utf8,${encodeURIComponent(taskTypeDefinition?.iconSvg || summaryTaskSvg)}`}
          alt=""
          style={{
            maxWidth: "80%", // Ensure the image does not exceed the container's width
            maxHeight: "100%", // Ensure the image does not exceed the container's height
            objectFit: "contain" // Maintain aspect ratio while fitting within the container
          }}
        />
        <i
          className={`icon-eye tc-icon-eye-visibility-off-download off-icon`}
          style={{
            maxWidth: "75%", // Ensure the image does not exceed the container's width
            maxHeight: "100%", // Ensure the image does not exceed the container's height
            objectFit: "contain" // Maintain aspect ratio while fitting within the container
          }}
        ></i>
      </span> */}
      <span
        slot="itemIcon"
        style={{
          height: "32px",
          width: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginLeft: "0px"
        }}
      >
        <div className="icon-container">
          <i className={`icon-eye tc-icon-eye-visibility-off-download off-icon`}></i>
          <img
            src={`data:image/svg+xml;utf8,${encodeURIComponent(taskTypeDefinition?.iconSvg || summaryTaskSvg)}`}
            alt=""
            className={`item-icon`}
          />
        </div>
      </span>
      {addDummyNode() ? (
        <ModusTreeViewItem key={`${taskTypeId}-dummy`} nodeId={`${taskTypeId}-dummy`} label="Loading..." />
      ) : (
        (taskTypeSubTree || taskType)?.children?.map((child) => (
          <TaskTreeItemComponent
            key={child.header.taskId}
            taskType={child}
            taskTypeId={child.header.taskId}
            parentExpanded={expanded}
            setSelectedNode={setSelectedNode}
          />
        ))
      )}
    </ModusTreeViewItem>
  )
}

export default TaskTreeItemComponent
